import { FormEvent, useMemo, useState } from "react";
import { Head, router } from "@inertiajs/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface Field {
  key: string;
  label: string;
  type: string;
  span: number;
  readonly: boolean;
  multiple?: boolean;
  options?: Array<{ value: string; label: string; color?: string; class?: string }>;
}

interface Section {
  name?: string | null;
  fields: Field[];
}

interface Tab {
  name: string;
  sections: Section[];
}

interface Resource {
  name: string;
  label: string;
  form_layout: Tab[];
  breadcrumb_field?: string;
  paths: {
    index: string;
    show?: string;
    update?: string;
  };
}

interface Props {
  resource: Resource;
  record: Record<string, unknown>;
  errors?: Record<string, string[]>;
}

const toInputType = (field: Field) => {
  if (field.key.toLowerCase().includes("email")) return "email";

  switch (field.type) {
    case "integer":
    case "float":
    case "decimal":
      return "number";
    case "date":
      return "date";
    case "datetime":
    case "datetime_with_timezone":
      return "datetime-local";
    default:
      return "text";
  }
};

type FormValue = string | boolean | string[];

const normalizeValue = (value: unknown, field: Field): FormValue => {
  if (field.type === "boolean") return Boolean(value);
  if (field.type === "relation" && field.multiple) {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.map((entry) => String(entry));
    return value === "" ? [] : [String(value)];
  }
  if (value === null || value === undefined) return "";

  if (field.type === "date") {
    if (typeof value === "string") return value.split("T")[0];
  }

  if (field.type === "datetime" || field.type === "datetime_with_timezone") {
    if (typeof value === "string") return value.replace("Z", "").slice(0, 16);
  }

  return String(value);
};

const spanClass = (span: number) => {
  if (span >= 3) return "md:col-span-2 xl:col-span-3";
  if (span === 2) return "md:col-span-2 xl:col-span-2";
  return "md:col-span-1 xl:col-span-1";
};

export default function Show({ resource, record, errors = {} }: Props) {
  const tabs = resource.form_layout;
  const [activeTab, setActiveTab] = useState(0);
  const recordId = record.id as string | number | undefined;
  const updatePath = useMemo(() => {
    if (!recordId || !resource.paths.update) return "";
    return resource.paths.update.replace("__ID__", String(recordId));
  }, [recordId, resource.paths.update]);

  const formKey = useMemo(() => {
    const fieldKey = tabs
      .flatMap((tab) => tab.sections.flatMap((section) => section.fields))
      .map((field) => field.key)
      .join(",");
    return `${recordId ?? "record"}-${fieldKey}`;
  }, [recordId, tabs]);

  const recordLabel =
    (record.name as string | undefined) ||
    (record.email as string | undefined) ||
    (recordId ? `#${recordId}` : resource.label);

  const handleSubmit = (payload: Record<string, FormValue>) => {
    if (!updatePath) return;

    router.put(
      updatePath,
      { record: payload },
      {
        preserveScroll: true,
      }
    );
  };

  return (
    <>
      <Head title={recordLabel} />

      <div className="space-y-4">
        <div className="border-b">
          <div className="flex flex-wrap gap-6 text-sm">
            {tabs.map((tab, index) => {
              const isActive = index === activeTab;
              return (
                <button
                  key={tab.name}
                  type="button"
                  className={
                    isActive
                      ? "border-b-2 border-primary pb-3 font-medium text-foreground"
                      : "py-3 text-muted-foreground hover:text-foreground"
                  }
                  onClick={() => setActiveTab(index)}
                >
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>
        <FormFields
          key={formKey}
          tabs={tabs}
          activeTab={activeTab}
          record={record}
          errors={errors}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}

function FormFields({
  tabs,
  activeTab,
  record,
  errors,
  onSubmit,
}: {
  tabs: Tab[];
  activeTab: number;
  record: Record<string, unknown>;
  errors: Record<string, string[]>;
  onSubmit: (payload: Record<string, FormValue>) => void;
}) {
  const allFields = useMemo(
    () => tabs.flatMap((tab) => tab.sections.flatMap((section) => section.fields)),
    [tabs]
  );
  const [formData, setFormData] = useState<Record<string, FormValue>>(() => {
    const nextData: Record<string, FormValue> = {};
    allFields.forEach((field) => {
      nextData[field.key] = normalizeValue(record[field.key], field);
    });
    return nextData;
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const payload: Record<string, FormValue> = {};
    allFields.forEach((field) => {
      if (field.readonly) return;
      payload[field.key] = formData[field.key];
    });
    onSubmit(payload);
  };

  return (
    <form id="resource-form" onSubmit={handleSubmit} className="pt-4">
      <div className="mx-auto w-full max-w-5xl space-y-8 px-2">
        {(tabs[activeTab]?.sections || []).map((section, sectionIndex) => (
          <div key={`${section.name || "section"}-${sectionIndex}`} className="space-y-4">
            {section.name ? (
              <h3 className="text-sm font-medium text-foreground">{section.name}</h3>
            ) : (
              sectionIndex > 0 && <div className="h-px bg-border" />
            )}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {section.fields.map((field) => {
                const inputType = toInputType(field);
                const fieldErrors = errors[field.key];
                const value = formData[field.key];
                const isReadonly = field.readonly;

                if (field.type === "boolean") {
                  return (
                    <div key={field.key} className={`space-y-2 ${spanClass(field.span)}`}>
                      <Label htmlFor={field.key} className="text-sm text-muted-foreground">
                        {field.label}
                      </Label>
                      <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-3">
                        <Checkbox
                          id={field.key}
                          checked={Boolean(value)}
                          disabled={isReadonly}
                          onCheckedChange={(checked) => {
                            if (isReadonly) return;
                            setFormData((prev) => ({
                              ...prev,
                              [field.key]: Boolean(checked),
                            }));
                          }}
                        />
                        <span className="text-sm">{isReadonly ? "عرض فقط" : "تفعيل"}</span>
                      </div>
                      {fieldErrors && <p className="text-sm text-destructive">{fieldErrors[0]}</p>}
                    </div>
                  );
                }

                const isRelation = field.type === "relation";
                const isMultiSelect = isRelation && field.multiple;
                const selectValue = isMultiSelect
                  ? Array.isArray(value)
                    ? value
                    : []
                  : typeof value === "string"
                    ? value
                    : "";
                const selectBaseClass = isMultiSelect
                  ? "min-h-[8rem] w-full rounded-md border border-input bg-input px-3 py-2 text-sm text-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                  : "h-9 w-full rounded-md border border-input bg-input px-3 text-sm text-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50";

                return (
                  <div key={field.key} className={`space-y-2 ${spanClass(field.span)}`}>
                    <Label htmlFor={field.key} className="text-sm text-muted-foreground">
                      {field.label}
                    </Label>
                    {(field.type === "enum" || isRelation) && field.options ? (
                      <select
                        id={field.key}
                        multiple={isMultiSelect}
                        value={selectValue}
                        aria-readonly={isReadonly}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            [field.key]: isMultiSelect
                              ? Array.from(event.target.selectedOptions).map(
                                  (option) => option.value
                                )
                              : event.target.value,
                          }))
                        }
                        className={selectBaseClass}
                        disabled={isReadonly}
                      >
                        {isRelation && !isMultiSelect && <option value="">--</option>}
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id={field.key}
                        type={inputType}
                        value={typeof value === "string" ? value : ""}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            [field.key]: event.target.value,
                          }))
                        }
                        readOnly={isReadonly}
                        disabled={isReadonly}
                      />
                    )}
                    {fieldErrors && <p className="text-sm text-destructive">{fieldErrors[0]}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </form>
  );
}
